import React, { useState } from 'react';
import participantService from '../services/participantService';
import couponService from '../services/couponService';
import petflixAuthService from '../services/petflixAuthService';
import { useI18n } from '../i18n';

interface ParticipantTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  contestId: string;
  entranceFee: number;
  contestName: string;
  onSuccess: (result: any) => void;
  onError: (error: string) => void;
}

const ParticipantTypeModal: React.FC<ParticipantTypeModalProps> = ({
  isOpen,
  onClose,
  contestId,
  entranceFee,
  contestName,
  onSuccess,
  onError
}) => {
  const { t } = useI18n();
  const [participantType, setParticipantType] = useState<'payment' | 'coupon'>('payment');
  const [couponCode, setCouponCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [couponInfo, setCouponInfo] = useState<any>(null);
  const [validationError, setValidationError] = useState('');
  const [assignedPassCode, setAssignedPassCode] = useState<any>(null);
  const [assignedPassCodes, setAssignedPassCodes] = useState<any[]>([]);
  const [participantCreated, setParticipantCreated] = useState(false);
  const [contestFull, setContestFull] = useState(false);
  const [numberOfParticipants, setNumberOfParticipants] = useState<number>(1);

  const handleCouponValidation = async () => {
    if (!couponCode.trim()) {
      setValidationError(t('participantmodal.pleaseEnterCode'));
      return;
    }

    if (!contestId) {
      setValidationError('Contest ID is required');
      return;
    }

    setIsValidating(true);
    setValidationError('');

    try {
      const result = await couponService.validateCouponCode(couponCode, contestId);
      if (result.success && result.valid) {
        setCouponInfo(result);
        setValidationError('');
      } else {
        setValidationError(result.error || t('participantmodal.errorValidatingCode'));
        setCouponInfo(null);
      }
    } catch (error: any) {
      setValidationError(error.message || t('participantmodal.errorValidatingCode'));
      setCouponInfo(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleCreateParticipant = async () => {
    setIsCreating(true);

    try {
      let result;

      if (participantType === 'payment') {
        try {
          const createdParticipants = [];
          const passCodes = [];
          
          // Crear múltiples participantes
          for (let i = 0; i < numberOfParticipants; i++) {
            try {
              // Obtener y asignar un pase disponible
              const passCodeResult = await couponService.getAndUsePassCode(contestId);
              const passCode = passCodeResult.data.passCode;
              passCodes.push(passCode);
              
              // Obtener el email del usuario logueado
              let userEmail = null;
              try {
                const userInfo = petflixAuthService.getUserInfo();
                if (userInfo && userInfo.email) {
                  userEmail = userInfo.email;
                }
              } catch (e) {
                console.warn('No se pudo obtener el email del usuario logueado:', e);
              }
              
              // Crear participante con pago normal
              const participantResult = await participantService.simulateParticipant(contestId, entranceFee, userEmail);
              createdParticipants.push({
                ...participantResult,
                assignedPassCode: passCode
              });
            } catch (error: any) {
              // Si no hay pases disponibles, mostrar mensaje de concurso lleno
              if (error.message && error.message.includes('No hay pases disponibles')) {
                setContestFull(true);
                setIsCreating(false);
                return; // No crear más participantes si el concurso está lleno
              }
              throw error; // Re-lanzar otros errores
            }
          }
          
          // Guardar todos los códigos de pase asignados
          setAssignedPassCodes(passCodes);
          // Mantener compatibilidad con el código existente (último código)
          setAssignedPassCode(passCodes[passCodes.length - 1]);
          
          // Mostrar el resultado pero NO cerrar el modal
          setParticipantCreated(true);
          onSuccess({
            participants: createdParticipants,
            count: createdParticipants.length,
            assignedPassCodes: passCodes,
            assignedPassCode: passCodes[passCodes.length - 1]
          });
          
          // NO llamar onClose() aquí - el usuario debe cerrar manualmente
        } catch (error: any) {
          // Si no hay pases disponibles, mostrar mensaje de concurso lleno
          if (error.message && error.message.includes('No hay pases disponibles')) {
            setContestFull(true);
            return; // No crear participante si el concurso está lleno
          }
          throw error; // Re-lanzar otros errores
        }
      } else {
        // Validar código antes de crear participante
        if (!couponInfo) {
          await handleCouponValidation();
          if (!couponInfo) {
            setIsCreating(false);
            return;
          }
        }

        // Obtener el email y user_id del usuario logueado
        let userEmail = null;
        let userId = null;
        try {
          const userInfo = petflixAuthService.getUserInfo();
          if (userInfo) {
            if (userInfo.email) {
              userEmail = userInfo.email;
            }
            if (userInfo._id || userInfo.id) {
              userId = userInfo._id || userInfo.id;
            }
          }
        } catch (e) {
          console.warn('No se pudo obtener la información del usuario logueado:', e);
        }
        
        // Usar el código de cupón y crear participante
        // Pasar user_id y user_email para que se registre en CouponPass_Usage_Log
        await couponService.useCouponCode(couponCode, contestId, userId, userEmail);
        
        result = await participantService.simulateParticipant(contestId, 0, userEmail); // 0 porque es con cupón
        
        onSuccess(result);
        onClose(); // Solo cerrar para cupones
      }
    } catch (error: any) {
      onError(error.message || t('participantmodal.errorCreatingParticipant'));
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setParticipantType('payment');
    setCouponCode('');
    setCouponInfo(null);
    setValidationError('');
    setAssignedPassCode(null);
    setAssignedPassCodes([]);
    setParticipantCreated(false);
    setContestFull(false);
    setNumberOfParticipants(1);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999
      }}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '100%',
          maxWidth: '500px',
          margin: '16px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            {t('participantmodal.addParticipant')} - {contestName}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              color: '#9ca3af',
              cursor: 'pointer',
              padding: '4px'
            }}
            onMouseOver={(e) => e.target.style.color = '#4b5563'}
            onMouseOut={(e) => e.target.style.color = '#9ca3af'}
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Selección de tipo */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              {t('participantmodal.participationType')}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="radio"
                  value="payment"
                  checked={participantType === 'payment'}
                  onChange={(e) => setParticipantType(e.target.value as 'payment' | 'coupon')}
                  style={{ marginRight: '8px' }}
                />
                <span>{t('participantmodal.paidParticipant')} (${entranceFee})</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="radio"
                  value="coupon"
                  checked={participantType === 'coupon'}
                  onChange={(e) => setParticipantType(e.target.value as 'payment' | 'coupon')}
                  style={{ marginRight: '8px' }}
                />
                <span>{t('participantmodal.couponPass')}</span>
              </label>
            </div>
          </div>

          {/* Campo para número de participantes (solo para participantes normales) */}
          {participantType === 'payment' && (
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('participantmodal.numberOfParticipants') || 'Número de Participantes'}
              </label>
              <input
                type="number"
                min="1"
                value={numberOfParticipants}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setNumberOfParticipants(Math.max(1, value));
                }}
                disabled={isCreating || participantCreated || contestFull}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>
          )}

          {/* Información del pase asignado */}
          {participantType === 'payment' && (assignedPassCode || assignedPassCodes.length > 0) && (
            <div style={{ marginTop: '8px', padding: '16px', backgroundColor: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '20px' }}></div>
                <p style={{ fontSize: '16px', color: '#166534', margin: 0, fontWeight: 'bold' }}>
                  {t('participantmodal.enteredContest')}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>{t('participantmodal.passCode')}</span>
                {assignedPassCodes.length > 0 ? (
                  <div 
                    style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      paddingRight: '8px'
                    }}
                  >
                    {assignedPassCodes.map((passCode: any, index: number) => (
                      <code 
                        key={index}
                        style={{ 
                          fontSize: '14px', 
                          fontFamily: 'monospace', 
                          backgroundColor: '#e5e7eb', 
                          padding: '6px 12px', 
                          borderRadius: '6px',
                          fontWeight: 'bold',
                          color: '#111827',
                          border: '1px solid #d1d5db',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {passCode.code}
                      </code>
                    ))}
                  </div>
                ) : assignedPassCode && (
                  <code style={{ 
                    fontSize: '16px', 
                    fontFamily: 'monospace', 
                    backgroundColor: '#e5e7eb', 
                    padding: '6px 12px', 
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    color: '#111827',
                    border: '1px solid #d1d5db'
                  }}>
                    {assignedPassCode.code}
                  </code>
                )}
              </div>
            </div>
          )}

          {/* Mensaje de concurso lleno */}
          {participantType === 'payment' && contestFull && (
            <div style={{ marginTop: '8px', padding: '16px', backgroundColor: '#fef2f2', border: '2px solid #fecaca', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '20px' }}>🚫</div>
                <p style={{ fontSize: '16px', color: '#dc2626', margin: 0, fontWeight: 'bold' }}>
                  {t('participantmodal.contestFull')}
                </p>
              </div>
              <p style={{ fontSize: '14px', color: '#7f1d1d', margin: 0 }}>
                {t('participantmodal.contestFullMessage')}
              </p>
            </div>
          )}

          {/* Campo de código de cupón */}
          {participantType === 'coupon' && (
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                {t('participantmodal.couponCode')}
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder={t('participantmodal.enterCode')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    outline: 'none',
                    fontSize: '14px'
                  }}
                  disabled={isValidating || isCreating}
                />
                <button
                  onClick={handleCouponValidation}
                  disabled={isValidating || isCreating || !couponCode.trim()}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    opacity: (isValidating || isCreating || !couponCode.trim()) ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!e.target.disabled) e.target.style.backgroundColor = '#6d28d9';
                  }}
                  onMouseOut={(e) => {
                    if (!e.target.disabled) e.target.style.backgroundColor = '#7c3aed';
                  }}
                >
                  {isValidating ? t('participantmodal.validating') : t('participantmodal.validate')}
                </button>
              </div>
              
              {/* Información del cupón */}
              {couponInfo && couponInfo.valid && couponInfo.coupon_code && (
                <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px' }}>
                  <p style={{ fontSize: '14px', color: '#166534', margin: 0 }}>
                    {t('participantmodal.validCode')} {couponInfo.coupon_code.uses_remaining || couponInfo.uses_remaining || 0}
                  </p>
                </div>
              )}

              {/* Error de validación */}
              {validationError && (
                <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px' }}>
                  <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>{validationError}</p>
                </div>
              )}
            </div>
          )}

          {/* Botones de acción */}
          <div style={{ display: 'flex', gap: '12px', paddingTop: '16px' }}>
            <button
              onClick={handleClose}
              style={{
                flex: 1,
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                opacity: isCreating ? 0.5 : 1
              }}
              onMouseOver={(e) => {
                if (!isCreating) e.target.style.backgroundColor = '#f9fafb';
              }}
              onMouseOut={(e) => {
                if (!isCreating) e.target.style.backgroundColor = 'white';
              }}
              disabled={isCreating}
            >
              {t('participantmodal.cancel')}
            </button>
            <button
              onClick={participantCreated || contestFull ? handleClose : handleCreateParticipant}
              disabled={
                isCreating || 
                (participantType === 'coupon' && (!couponInfo || couponInfo.uses_remaining <= 0))
              }
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: participantCreated ? '#059669' : contestFull ? '#dc2626' : '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                opacity: (isCreating || (participantType === 'coupon' && (!couponInfo || couponInfo.uses_remaining <= 0))) ? 0.5 : 1
              }}
              onMouseOver={(e) => {
                if (!e.target.disabled) {
                  if (participantCreated) e.target.style.backgroundColor = '#047857';
                  else if (contestFull) e.target.style.backgroundColor = '#b91c1c';
                  else e.target.style.backgroundColor = '#6d28d9';
                }
              }}
              onMouseOut={(e) => {
                if (!e.target.disabled) {
                  if (participantCreated) e.target.style.backgroundColor = '#059669';
                  else if (contestFull) e.target.style.backgroundColor = '#dc2626';
                  else e.target.style.backgroundColor = '#7c3aed';
                }
              }}
            >
              {isCreating ? t('participantmodal.creating') : participantCreated ? t('participantmodal.close') : contestFull ? t('participantmodal.close') : t('participantmodal.createParticipant')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantTypeModal;
